import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Loader2, ImageOff, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin — AI Ambassador showcase media audit.
 * Read-only preview of every generated headshot with assignment status so
 * admins can identify owners of the unassigned/orphan images. No delete/reassign.
 */
const Tile = ({ item }) => {
  const assigned = item.assigned;
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid={`media-tile-${item.filename}`}>
      <div className="aspect-square bg-slate-100">
        <img src={`${BACKEND}${item.url}`} alt={item.filename} className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="p-2.5">
        {assigned ? (
          <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[11px] flex items-center gap-1 w-fit">
            <CheckCircle2 className="w-3 h-3" /> {assigned.name || 'Assigned'}
          </Badge>
        ) : (
          <Badge className="bg-amber-100 text-amber-700 border-0 text-[11px] flex items-center gap-1 w-fit">
            <ImageOff className="w-3 h-3" /> Unassigned
          </Badge>
        )}
        <p className="text-[10px] text-gray-400 mt-1.5 font-mono break-all">{item.filename}</p>
        {assigned?.slug && <p className="text-[10px] text-gray-400">/{assigned.slug}</p>}
        <p className="text-[10px] text-gray-400">source: {item.source}</p>
      </div>
    </div>
  );
};

const AmbassadorMediaAudit = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/admin/ambassador-media/audit');
        setData(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load media audit');
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-cyan-500" /></div>;
  if (!data) return null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="ambassador-media-audit-page">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="audit-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-lg font-bold text-gray-900 leading-tight">AI Ambassador Media Audit</h1>
            <p className="text-xs text-gray-500">
              {data.total_files} images · {data.assigned_count} assigned · {data.orphan_count} unassigned
            </p>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-8">
        <section data-testid="orphan-section">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Unassigned headshots</h2>
            <Badge className="bg-amber-500 text-white border-0">{data.orphan_count}</Badge>
          </div>
          {data.orphans.length === 0 ? (
            <Card><CardContent className="py-6 text-center text-gray-500 text-sm">No unassigned images.</CardContent></Card>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {data.orphans.map((item) => <Tile key={item.filename} item={item} />)}
            </div>
          )}
        </section>

        <section data-testid="assigned-section">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Assigned to ambassadors</h2>
            <Badge className="bg-emerald-500 text-white border-0">{data.assigned_count}</Badge>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {data.assigned.map((item) => <Tile key={item.filename} item={item} />)}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AmbassadorMediaAudit;
